import React, { useState } from "react";
import { Check, CreditCard, Crown, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { updateSubscription } from "../../api/api";
import { useAuth } from "../../context/AuthContext";
import { useUpsell } from "./UpsellProvider";
import { canUseMockBilling } from "./billingVisibility";
import { useLanguage } from "../../i18n/LanguageContext";

const ENABLE_MOCK_BILLING = process.env.REACT_APP_ENABLE_MOCK_BILLING === "true";

const isDemoMode = () => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem("demoMode") === "1";
};

const UpsellModal = () => {
  const upsell = useUpsell();
  const queryClient = useQueryClient();
  const { refreshUser, user } = useAuth();
  const { t } = useLanguage();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [error, setError] = useState(null);
  const demo = isDemoMode();
  const showMockUpgrade = canUseMockBilling({
    enabled: ENABLE_MOCK_BILLING,
    demo,
    user,
  });

  if (!upsell?.isOpen) {
    return null;
  }

  const handleMockUpgrade = async () => {
    setIsUpgrading(true);
    setError(null);
    try {
      await updateSubscription({ tier: "premium" });
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ["me"] });
      upsell.close();
    } catch (err) {
      setError(t("paywall.error"));
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleSignup = () => {
    window.localStorage.removeItem("demoMode");
    window.localStorage.removeItem("reflecta.demo.startedAt");
    window.location.href = "/auth?mode=signup";
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
        <div className="relative border-b border-gray-100 bg-gradient-to-br from-purple-50 via-white to-white px-6 py-5">
          <button
            type="button"
            onClick={upsell.close}
            className="absolute right-5 top-5 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
            aria-label={t("common.close")}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="pr-12">
            <div className="mb-3 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
              <Crown className="h-3 w-3" /> {t("common.premium")}
            </div>
            <h2 className="text-xl font-extrabold tracking-tight text-gray-900">{t("paywall.title")}</h2>
            <p className="mt-1 text-sm text-gray-500">{t("paywall.price")}</p>
          </div>
        </div>

        <div className="px-6 py-5">
          <ul className="space-y-3 text-sm text-gray-700">
            {[
              t("paywall.featureChat"),
              t("paywall.featureGoals"),
              t("paywall.featureAnalytics"),
              t("paywall.featureQuestions"),
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {upsell.reason && (
            <p className="mt-5 rounded-xl border border-purple-100 bg-purple-50 px-4 py-3 text-sm font-medium text-[#6A0DAD]">
              {upsell.reason}
            </p>
          )}

          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-100 px-6 py-5">
          <button
            type="button"
            onClick={demo ? handleSignup : undefined}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
          >
            <CreditCard className="h-4 w-4" />
            {demo ? t("demo.createAccount") : t("common.goPremium")}
          </button>
          {showMockUpgrade && (
            <button
              type="button"
              onClick={handleMockUpgrade}
              disabled={isUpgrading}
              className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
            >
              {isUpgrading ? t("paywall.updating") : t("paywall.mockUpgrade")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpsellModal;
